import { useCallback, useEffect, useState } from 'react';
import Button from '../../../external/components/Button/Button';
import {
  StyledDd,
  StyledDefWrapper,
  StyledDl,
  StyledDt,
} from '../../../external/components/DefLists';
import { TextInput } from '../../../external/components/Input/Input';
import { Skeleton } from '../../../external/components/SkeletonLoading/Skeleton';
import { Tag } from '../../../external/components/Tag/Tag';
import Text from '../../../external/components/Text/Text';
import { ProviderRpcError } from '../../../types/metamask.types';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { timeHelpers } from '../helpers/timeHelpers';
import { useNakedStakingPool } from '../hooks/usePool';
import { useStakingActions } from '../hooks/useStakingAction';
import { useStakingData } from '../hooks/useStakingData';
import { StakingScreenEnum } from './_Screens';

interface Props {
  unstakeAmount: string;
  setUnstakeAmount: React.Dispatch<React.SetStateAction<string>>;
  setScreen: React.Dispatch<React.SetStateAction<StakingScreenEnum>>;
}

export function UnstakeScreen({ unstakeAmount, setScreen, setUnstakeAmount }: Props) {
  const [unstakeLoading, setUnstakeLoading] = useState(false);
  const [unstakeImmediatelyLoading, setUnstakeImmediatelyLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const pool = useNakedStakingPool();

  const { stakingTokenSymbol } = pool;

  const { fee: poolFee, getFees } = useStakingData({
    pool,
  });

  const { unstakeImmediately, unstake } = useStakingActions({
    pool,
  });

  const { unstakeData, stakedBalance } = pool;

  const fee = pool.fee > 0 ? pool.fee : poolFee;

  const unstakingDurationHumanised = timeHelpers.humaniseTime(unstakeData.unstakingDuration);
  const unstakeAmountGreaterThanStaked = BigNumberHelper.isGreaterThan(
    unstakeAmount,
    stakedBalance || 0,
  );
  const unstakeAmountLessThanOrZero = BigNumberHelper.isLessThanOrEqualTo(unstakeAmount || 0, 0);

  const disabled =
    unstakeAmountLessThanOrZero ||
    unstakeAmountGreaterThanStaked ||
    Number.isNaN(unstakeAmount) ||
    unstakeImmediatelyLoading ||
    unstakeLoading ||
    loading;

  const handleUnstakeImmediately = async () => {
    setUnstakeImmediatelyLoading(true);

    try {
      await unstakeImmediately(unstakeAmount);
      setUnstakeAmount(unstakeAmount);
      setUnstakeImmediatelyLoading(false);

      setScreen(StakingScreenEnum.UNSTAKE_IMMEDIATELY_SUCCESS);
    } catch (_error) {
      setUnstakeImmediatelyLoading(false);
      const error = _error as ProviderRpcError;
      if (error.code === 4001) {
        // do nothing if RPC error
        return;
      } else {
        setScreen(StakingScreenEnum.ERROR);
      }
    }
  };

  const handleUnstake = async () => {
    setUnstakeLoading(true);
    try {
      await unstake(unstakeAmount);
      setUnstakeAmount(unstakeAmount);

      setUnstakeLoading(false);
      setScreen(StakingScreenEnum.UNSTAKE_SUCCESS);
    } catch (_error) {
      setUnstakeLoading(false);
      const error = _error as ProviderRpcError;
      if (error.code === 4001) {
        // do nothing if RPC error
        return;
      } else {
        setScreen(StakingScreenEnum.ERROR);
      }
    }
  };

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const regex = new RegExp('^[0-9]*[.,]?[0-9]*$');
    if (regex.test(target.value)) {
      setUnstakeAmount(target.value);
    }
  };

  const init = useCallback(async () => {
    setLoading(true);
    try {
      await getFees();
    } catch (error) {
      //handle error
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (pool.fee <= 0) {
      init();
    }
  }, []);

  return (
    <>
      <Button
        size='small'
        variant='secondary'
        dark={true}
        onClick={() => setScreen(StakingScreenEnum.HOME_SCREEN)}
      >
        ⬅
      </Button>
      <StyledDl>
        <StyledDefWrapper>
          <StyledDt>
            <Text as='span' dark={true} weight={600}>
              Current amount staked
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              {loading ? (
                <Skeleton height='18px' width='100px' dark={true} />
              ) : (
                <Text as='span' dark={true}>
                  {stakedBalance} {stakingTokenSymbol}
                </Text>
              )}
            </>
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text as='span' dark={true} weight={600}>
              Current amount unstaking
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              {loading ? (
                <Skeleton height='18px' width='100px' dark={true} />
              ) : (
                <Text as='span' dark={true}>
                  {unstakeData.amount.toLocaleString('en-Us', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: pool.stakingTokenDecimals,
                  })}{' '}
                  {stakingTokenSymbol}
                </Text>
              )}
            </>
          </StyledDd>
        </StyledDefWrapper>
      </StyledDl>
      <Text dark={true} weight={600} style={{ marginBottom: '0.5rem' }}>
        <label htmlFor='unstake-amount'>Enter the amount you wish to unstake</label>
      </Text>
      <TextInput
        darkAlt={true}
        id='unstake-amount'
        type='text'
        disabled={unstakeLoading}
        value={unstakeAmount}
        onChange={handleChange}
        pattern={'^[0-9]*[.,]?[0-9]*$'}
        placeholder='Enter unstake amount'
        autoComplete='off'
      ></TextInput>
      {unstakeAmountGreaterThanStaked && (
        <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
          {' '}
          You do not have enough {stakingTokenSymbol}
        </Text>
      )}
      <br />{' '}
      <div className='flex justify-end'>
        <Tag dark={true} onClick={() => setUnstakeAmount(stakedBalance || '0')}>
          {' '}
          100%
        </Tag>
        <Tag
          dark={true}
          onClick={() => setUnstakeAmount(BigNumberHelper.divide(stakedBalance || '0', 4 / 3))}
        >
          {' '}
          75%
        </Tag>
        <Tag
          dark={true}
          onClick={() => setUnstakeAmount(BigNumberHelper.divide(stakedBalance || '0', 2))}
        >
          {' '}
          50%
        </Tag>
        <Tag
          dark={true}
          onClick={() => setUnstakeAmount(BigNumberHelper.divide(stakedBalance || '0', 4))}
        >
          {' '}
          25%
        </Tag>
      </div>
      <Text color='lightgrey' size='sm'>
        * There is an unstaking period of {unstakingDurationHumanised} before you can withdraw your
        staked tokens.
      </Text>
      <Text color='lightgrey' size='sm'>
        * Staking rewards will stop being earned as soon as you click "Confirm Unstake".
      </Text>
      <Text color='lightgrey' size='sm'>
        * If you are currently unstaking and decide to unstake more half way through, the timer will
        be reset to {unstakingDurationHumanised}, and you will be able to withdraw both set of
        tokens after {unstakingDurationHumanised}.
      </Text>
      <Text color='lightgrey' size='sm'>
        * There is an unstaking period of {unstakingDurationHumanised} before you can withdraw your
        staked tokens.
      </Text>
      <div className='flex direction-column align-center'>
        <Button dark={true} onClick={handleUnstake} disabled={disabled} loading={unstakeLoading}>
          {unstakeLoading
            ? 'Processing...'
            : unstakeData.amount > 0
            ? 'Reset timer and unstake'
            : 'Confirm unstake'}
        </Button>
        <Text color='lightgrey' size='sm'>
          or
        </Text>
        <Text dark={true} size='sm' style={{ maxWidth: '80%' }}>
          You are able to unstake your tokens immediately by <strong>paying a {fee}%</strong> fee on
          your current unstaking amount.
        </Text>
        <Button
          dark={true}
          variant='secondary'
          disabled={disabled}
          loading={unstakeImmediatelyLoading}
          onClick={handleUnstakeImmediately}
        >
          {unstakeImmediatelyLoading ? 'Processing...' : 'Unstake immediately'}
        </Button>
      </div>
    </>
  );
}
